import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PatternRecognition.css';

// Shape definitions for patterns
const SHAPES = [
  { id: 'circle', render: (color) => <div className="shape circle" style={{ backgroundColor: color }}></div> },
  { id: 'square', render: (color) => <div className="shape square" style={{ backgroundColor: color }}></div> },
  { id: 'triangle', render: (color) => <div className="shape triangle" style={{ borderBottomColor: color }}></div> },
  { id: 'diamond', render: (color) => <div className="shape diamond" style={{ backgroundColor: color }}></div> },
  { id: 'star', render: (color) => <div className="shape star" style={{ color: color }}>★</div> },
  { id: 'hexagon', render: (color) => <div className="shape hexagon" style={{ backgroundColor: color }}></div> },
];

const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#A29BFE'];

// Generate a pattern based on difficulty
const generatePattern = (difficulty) => {
  const patternLength = Math.min(3 + difficulty, 7);
  const pattern = [];

  // Create a simple repeating pattern with variations
  for (let i = 0; i < patternLength; i++) {
    pattern.push({
      shape: SHAPES[i % SHAPES.length].id,
      color: COLORS[i % COLORS.length],
    });
  }

  // The correct next item follows the pattern
  const nextItem = {
    shape: SHAPES[patternLength % SHAPES.length].id,
    color: COLORS[patternLength % COLORS.length],
  };

  // Generate 3 wrong options
  const options = [{ ...nextItem, isCorrect: true }];

  while (options.length < 4) {
    const wrongShape = SHAPES[Math.floor(Math.random() * SHAPES.length)].id;
    const wrongColor = COLORS[Math.floor(Math.random() * COLORS.length)];

    // Make sure it's not a duplicate
    const isDuplicate = options.some(
      (o) => o.shape === wrongShape && o.color === wrongColor
    );

    if (!isDuplicate) {
      options.push({ shape: wrongShape, color: wrongColor, isCorrect: false });
    }
  }

  // Shuffle options
  return {
    sequence: pattern,
    correctAnswer: nextItem,
    options: options.sort(() => Math.random() - 0.5),
  };
};

const PatternRecognition = ({ config, puzzleId, onComplete, onSkip }) => {
  const [currentRound, setCurrentRound] = useState(1);
  const [difficulty, setDifficulty] = useState(config?.startingDifficulty || 1);
  const [pattern, setPattern] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(config?.timePerRoundSeconds || 30);
  const [rounds, setRounds] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const roundStartTimeRef = useRef(Date.now());
  const gameStartTimeRef = useRef(Date.now());
  const timerRef = useRef(null);
  const handleAnswerRef = useRef(null);
  const totalRounds = config?.totalRounds || 5;

  // Generate new pattern for each round
  useEffect(() => {
    setPattern(generatePattern(difficulty));
    setTimeRemaining(config?.timePerRoundSeconds || 30);
    setSelectedOption(null);
    setShowResult(false);
    roundStartTimeRef.current = Date.now();
  }, [currentRound, difficulty, config?.timePerRoundSeconds]);

  const handleGameEnd = useCallback(
    (finalRounds) => {
      const correctAnswers = finalRounds.filter((r) => r.isCorrect).length;
      const totalTime = Math.round((Date.now() - gameStartTimeRef.current) / 1000);
      const highestDifficulty = Math.max(...finalRounds.map((r) => r.difficulty));

      const result = {
        puzzleId,
        totalRounds,
        correctAnswers,
        totalTimeTakenSeconds: totalTime,
        rounds: finalRounds,
        highestDifficultyReached: highestDifficulty,
      };

      console.log('Pattern recognition ended with result:', result);

      if (onComplete) {
        onComplete(result);
      }
    },
    [puzzleId, totalRounds, onComplete]
  );

  // Start timer when pattern is ready
  useEffect(() => {
    if (!pattern || showResult) return;

    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Use ref to get latest handleAnswer with current state values
          if (handleAnswerRef.current) {
            handleAnswerRef.current(null, true);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [pattern, showResult]);

  const handleAnswer = useCallback(
    (option, timedOut = false) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      const timeTaken = Math.round((Date.now() - roundStartTimeRef.current) / 1000);
      const correct = option?.isCorrect || false;

      setSelectedOption(option);
      setIsCorrect(correct);
      setShowResult(true);

      const roundResult = {
        roundNumber: currentRound,
        difficulty,
        patternSequence: pattern.sequence.map((p) => `${p.color}-${p.shape}`),
        options: pattern.options.map((o) => `${o.color}-${o.shape}`),
        correctAnswer: `${pattern.correctAnswer.color}-${pattern.correctAnswer.shape}`,
        userAnswer: option ? `${option.color}-${option.shape}` : 'timed_out',
        isCorrect: correct,
        timeTakenSeconds: timeTaken,
        timeAllowedSeconds: config?.timePerRoundSeconds || 30,
      };

      const newRounds = [...rounds, roundResult];
      setRounds(newRounds);

      // Move to next round after delay
      setTimeout(() => {
        // Adjust difficulty based on performance
        if (correct && difficulty < (config?.maxDifficulty || 5)) {
          setDifficulty((prev) => prev + 1);
        } else if (!correct && difficulty > 1) {
          setDifficulty((prev) => Math.max(1, prev - 1));
        }

        // Check if game is complete
        if (currentRound >= totalRounds) {
          handleGameEnd(newRounds);
        } else {
          setCurrentRound((prev) => prev + 1);
        }
      }, 1500);
    },
    [pattern, currentRound, difficulty, rounds, totalRounds, config, handleGameEnd]
  );

  // Keep ref updated with latest handleAnswer
  useEffect(() => {
    handleAnswerRef.current = handleAnswer;
  }, [handleAnswer]);

  const handleSkip = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (onSkip) {
      onSkip(puzzleId);
    }
  }, [puzzleId, onSkip]);

  const renderShape = (shapeId, color) => {
    const shape = SHAPES.find((s) => s.id === shapeId);
    return shape ? shape.render(color) : null;
  };

  if (!pattern) return null;

  const correctCount = rounds.filter((r) => r.isCorrect).length;

  return (
    <div className="pattern-recognition-overlay">
      <div className="pattern-recognition-container">
        <div className="pattern-header">
          <div className="puzzle-indicator">
            <span className="pulse-dot"></span>
            <span className="indicator-text">Pattern Recognition</span>
          </div>
          <div className="game-stats">
            <div className="stat-item">
              <span className="stat-label">Round</span>
              <span className="stat-value">
                {currentRound}/{totalRounds}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Difficulty</span>
              <span className="stat-value">{difficulty}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Time</span>
              <span className={`stat-value ${timeRemaining <= 10 ? 'warning' : ''}`}>
                {timeRemaining}s
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Score</span>
              <span className="stat-value">
                {correctCount}/{rounds.length}
              </span>
            </div>
          </div>
        </div>

        <div className="pattern-content">
          <div className="pattern-question">
            <h3>What comes next in the pattern?</h3>
          </div>

          <div className="pattern-sequence">
            {pattern.sequence.map((item, idx) => (
              <div key={idx} className="sequence-item">
                {renderShape(item.shape, item.color)}
              </div>
            ))}
            <div className="sequence-item question-mark">
              <span>?</span>
            </div>
          </div>

          <div className="pattern-options">
            {pattern.options.map((option, idx) => (
              <button
                key={idx}
                className={`option-button ${
                  showResult
                    ? option.isCorrect
                      ? 'correct'
                      : selectedOption === option
                      ? 'incorrect'
                      : ''
                    : ''
                } ${selectedOption === option ? 'selected' : ''}`}
                onClick={() => !showResult && handleAnswer(option)}
                disabled={showResult}
              >
                {renderShape(option.shape, option.color)}
              </button>
            ))}
          </div>

          {showResult && (
            <div className={`result-message ${isCorrect ? 'correct' : 'incorrect'}`}>
              {isCorrect ? 'Correct!' : 'Incorrect'}
            </div>
          )}
        </div>

        <div className="pattern-footer">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(currentRound / totalRounds) * 100}%` }}
            ></div>
          </div>
          <button className="skip-button" onClick={handleSkip}>
            Skip Puzzle
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatternRecognition;
